import { useState, useEffect, FormEvent } from 'react'
import api from '../../../services/api'
import Button from '../../../components/common/Button'
import FileUpload from '../../../components/common/FileUpload'
import { getImageUrl } from '../../../utils/helpers'

interface GalleryCategory {
  id: number
  name: string
  slug: string
}

interface GalleryItem {
  id: number
  file_name: string
  file_path: string
  mime_type: string
  title: string
  description: string | null
  category_id: number | null
  url: string
  is_video: boolean
}

interface GalleryFormProps {
  item?: GalleryItem
  categories: GalleryCategory[]
  onSubmit: (data: any) => void
  onCancel: () => void
  isNew?: boolean
}

function GalleryForm({ item, categories, onSubmit, onCancel, isNew = false }: GalleryFormProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: '' as string | number,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<any>(null)

  useEffect(() => {
    if (item) {
      setFormData({
        title: item.title || '',
        description: item.description || '',
        category_id: item.category_id || '',
      })
    }
  }, [item])

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis'
    }

    if (isNew && !uploadedFile) {
      newErrors.file = 'Un fichier est requis'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleFileUpload = (fileData: any) => {
    if (fileData && fileData.id) {
      setUploadedFile(fileData)
      // Auto-fill title from filename if empty
      if (!formData.title) {
        const nameWithoutExt = fileData.file_name?.replace(/\.[^/.]+$/, '') || ''
        setFormData({ ...formData, title: nameWithoutExt })
      }
      setErrors({ ...errors, file: '' })
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    setSubmitting(true)
    try {
      if (isNew && uploadedFile) {
        // Update the uploaded media to show in gallery
        await api.put(`/media/${uploadedFile.id}/gallery`, {
          title: formData.title,
          description: formData.description || null,
          category_id: formData.category_id || null,
          show_in_gallery: true,
        })
      }
      onSubmit(formData)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde')
    } finally {
      setSubmitting(false)
    }
  }

  const getMediaPreviewUrl = () => {
    if (uploadedFile?.file_path) {
      return getImageUrl(uploadedFile.file_path)
    }
    if (item?.file_path) {
      return getImageUrl(item.file_path)
    }
    return null
  }

  const isVideo = () => {
    if (uploadedFile?.mime_type) {
      return uploadedFile.mime_type.startsWith('video/')
    }
    return item?.is_video || false
  }

  return (
    <form onSubmit={handleSubmit} className="gallery-form">
      {/* File Upload (only for new items) */}
      {isNew && (
        <div className="form-field">
          <label>
            Fichier
            <span className="required">*</span>
          </label>
          <FileUpload
            onUpload={handleFileUpload}
            accept="image/*,video/mp4,video/webm,video/quicktime"
            label={uploadedFile?.file_name || 'Choisir une image ou vidéo'}
          />
          {errors.file && <span className="field-error">{errors.file}</span>}
        </div>
      )}

      {/* Media Preview */}
      {(item || uploadedFile) && getMediaPreviewUrl() && (
        <div className="media-preview">
          {isVideo() ? (
            <video src={getMediaPreviewUrl()!} muted />
          ) : (
            <img src={getMediaPreviewUrl()!} alt="Aperçu" />
          )}
          <div className="media-info">
            <div className="media-name">{uploadedFile?.file_name || item?.file_name}</div>
            <div className="media-type">
              {isVideo() ? 'Vidéo' : 'Image'}
            </div>
          </div>
        </div>
      )}

      {/* Title */}
      <div className="form-field">
        <label htmlFor="title">
          Titre
          <span className="required">*</span>
        </label>
        <input
          id="title"
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Titre du média"
        />
        {errors.title && <span className="field-error">{errors.title}</span>}
      </div>

      {/* Description */}
      <div className="form-field">
        <label htmlFor="description">Description (optionnel)</label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Description du média (sera affichée sous le titre dans la galerie)"
          rows={3}
        />
      </div>

      {/* Category */}
      <div className="form-field">
        <label htmlFor="category">Catégorie</label>
        <select
          id="category"
          value={formData.category_id}
          onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
        >
          <option value="">Sans catégorie</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>
      </div>

      {/* Actions */}
      <div className="form-actions">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Annuler
        </Button>
        <Button type="submit" variant="primary" disabled={submitting}>
          {submitting ? 'Enregistrement...' : isNew ? 'Ajouter' : 'Mettre à jour'}
        </Button>
      </div>
    </form>
  )
}

export default GalleryForm
